import {
  DwollaAccountKind,
  DwollaDocumentType,
} from '@thera-hr/api-sdk/api/resources/api/resources/thera/resources/dwolla';
import {
  AchProvider,
  AlloyDocumentRequest,
  AlloyKybDecision,
  AlloyKybInformation,
  CompanyLegalStructure,
  DwollaBeneficialOwnerInfo,
  DwollaBeneficialOwnershipStatus,
  DwollaCompanyOnboardingStage,
  DwollaCustomerStatus,
  DwollaVerificationDirective,
  KybBusinessDocumentKind,
  KybPersonDocumentKind,
  KybDocument,
} from '@thera-hr/api-sdk/api/resources/db';

import type { AchOriginator } from 'lib/constants';
import { THERA_ADVANCE_BYPASS_COMPANY_IDS, UPDATE_OWNERS_WITHOUT_TP_COMPANY_IDS } from 'lib/whitelistConstants';

// Every rule about which banking rail (Dwolla or Treasury Prime) a company is on, and how far along it is.
// Mirrors the backend: `dwollaBanking` wins over `theraBanking`, and bulk pay only uses a rail once it is live,
// otherwise it falls through to the next one and finally to the legacy Stripe pay-in.

export type PayrollBankingRail = 'DWOLLA' | 'TREASURY_PRIME';

/**
 * Why payroll is blocked while a company waits on its banking rail. Each NEEDS_ status has its own fix the admin can
 * make in the app. DECLINED and ON_HOLD need Thera support.
 */
export type PayrollBankingReviewStatus =
  | 'IN_REVIEW'
  | 'NEEDS_ACCOUNT_SETUP'
  | 'NEEDS_CERTIFICATION'
  | 'NEEDS_RETRY'
  | 'NEEDS_DOCUMENTS'
  | 'DECLINED'
  | 'ON_HOLD';

export interface DwollaAccount {
  onboardingStage?: DwollaCompanyOnboardingStage;
  balanceFundingSourceId?: string;
  businessClassification?: string;
  customerStatus?: DwollaCustomerStatus;
  businessType?: string;
  beneficialOwners?: DwollaBeneficialOwnerInfo[];
  beneficialOwnershipStatus?: DwollaBeneficialOwnershipStatus;
  verificationDirectives?: DwollaVerificationDirective[];
  verificationActions?: string[];
  // The SDK parses it into a Date; company data read as plain JSON keeps the ISO string.
  createdAt?: Date | string;
}

export interface PayrollBankingCompany {
  companyID?: string;
  theraBanking?: boolean;
  dwollaBanking?: boolean;
  tp?: {
    payrollFboOnboardingStage?: string;
    depositAccount?: { depositFboOnboardingStage?: string };
  };
  dwolla?: DwollaAccount & { cashAccount?: DwollaAccount };
  dwollaTermsAcceptance?: { accepted: boolean };
  alloyKyb?: AlloyKybInformation;
  kybDocuments?: KybDocument[];
  companyBankingDetails?: { legalStructure?: string };
}

/** The Dwolla industry the company picked: the customer's once it exists, else the one saved with the KYB details. */
export const getSavedBusinessClassification = (
  company?: {
    dwolla?: { businessClassification?: string };
    companyBankingDetails?: { businessClassification?: string };
  } | null,
): string | undefined =>
  company?.dwolla?.businessClassification || company?.companyBankingDetails?.businessClassification || undefined;

// A verified customer without a Dwolla Balance can't receive funds yet, so it stays in review.
const DWOLLA_REVIEW_STATUS: Record<DwollaCompanyOnboardingStage, PayrollBankingReviewStatus> = {
  CUSTOMER_CREATED: 'IN_REVIEW',
  BENEFICIAL_OWNERS_ADDED: 'IN_REVIEW',
  OWNERSHIP_CERTIFIED: 'IN_REVIEW',
  VERIFIED: 'IN_REVIEW',
  NEEDS_RETRY: 'NEEDS_RETRY',
  NEEDS_DOCUMENTS: 'NEEDS_DOCUMENTS',
  AWAITING_CERTIFICATION: 'NEEDS_CERTIFICATION',
  SUSPENDED: 'ON_HOLD',
  DEACTIVATED: 'ON_HOLD',
};

export type AlloyKybReviewStatus = Extract<
  PayrollBankingReviewStatus,
  'IN_REVIEW' | 'NEEDS_ACCOUNT_SETUP' | 'NEEDS_RETRY' | 'DECLINED'
>;

// An approved check still leaves the admin to open the payroll account, because the backend doesn't open it on approval.
const ALLOY_KYB_REVIEW_STATUS: Record<AlloyKybDecision, AlloyKybReviewStatus> = {
  PENDING: 'IN_REVIEW',
  APPROVED: 'NEEDS_ACCOUNT_SETUP',
  ERROR: 'NEEDS_RETRY',
  DENIED: 'DECLINED',
};

/** Legal structures the backend can map to a Dwolla business type. Everything else is rejected at onboarding. */
export const DWOLLA_SUPPORTED_LEGAL_STRUCTURES: readonly CompanyLegalStructure[] = [
  'llc',
  'ccorp',
  'corp',
  'scorp',
  'nonprofit',
  'partnership',
  'llp',
  'lp',
  'soleprop',
  'trust',
];

export const isDwollaSupportedLegalStructure = (legalStructure?: string): boolean =>
  DWOLLA_SUPPORTED_LEGAL_STRUCTURES.includes(legalStructure as CompanyLegalStructure);

// Dwolla onboards these as sole proprietorships, which don't certify beneficial ownership.
const DWOLLA_SOLE_PROPRIETORSHIP_STRUCTURES: readonly CompanyLegalStructure[] = ['soleprop', 'trust'];

/** Mirrors the backend: every Dwolla business type certifies beneficial ownership except a sole proprietorship. */
export const requiresDwollaOwnershipCertification = (legalStructure?: string): boolean =>
  !DWOLLA_SOLE_PROPRIETORSHIP_STRUCTURES.includes(legalStructure as CompanyLegalStructure);

export const isDwollaAccountReady = (account?: DwollaAccount): boolean =>
  account?.onboardingStage === 'VERIFIED' && Boolean(account?.balanceFundingSourceId);

export const isTpPayrollApproved = (company?: PayrollBankingCompany): boolean =>
  company?.tp?.payrollFboOnboardingStage === 'DISBURSEMENT_ACCOUNT_APPROVED';

/** The rail the company onboards to. */
export const getOnboardingRail = (company?: PayrollBankingCompany): PayrollBankingRail | null => {
  if (company?.dwollaBanking === true) return 'DWOLLA';
  if (company?.theraBanking === true) return 'TREASURY_PRIME';
  return null;
};

/**
 * Who debits the company's bank for an ACH provider the backend filed. On the Dwolla rail Dwolla does the payroll
 * debits, whether the bank was filed under Treasury Prime or Stripe. Check debits W-2 payroll itself.
 */
export const getAchDebitOriginator = (
  company: PayrollBankingCompany | undefined,
  provider: AchProvider,
): AchOriginator => (provider !== 'CHECK' && getOnboardingRail(company) === 'DWOLLA' ? 'DWOLLA' : provider);

/** An admin accepted the updated Terms of Service. Dwolla onboarding and Dwolla payroll both wait for it. */
export const hasAcceptedDwollaTerms = (company?: PayrollBankingCompany): boolean =>
  company?.dwollaTermsAcceptance?.accepted === true;

/**
 * The company sent its details to Dwolla from its old rail (TP or Stripe), and payroll stays there until ops switches
 * it over once Dwolla verifies it. Before the Dwolla customer exists the move shows as the KYB check. TP signup runs the
 * check too, so only the Dwolla terms, which the move accepts first and TP signup never asks for, mark it as a move.
 */
export const isMovingToDwolla = (company?: PayrollBankingCompany): boolean =>
  company?.dwollaBanking !== true &&
  (Boolean(company?.dwolla) || (Boolean(company?.alloyKyb) && hasAcceptedDwollaTerms(company)));

/** The rail bulk pay uses right now, or null when it would fall through to the legacy pay-in. */
export const getActivePayrollRail = (company?: PayrollBankingCompany): PayrollBankingRail | null => {
  if (company?.dwollaBanking === true && hasAcceptedDwollaTerms(company) && isDwollaAccountReady(company.dwolla)) {
    return 'DWOLLA';
  }
  if (company?.theraBanking === true && isTpPayrollApproved(company)) return 'TREASURY_PRIME';
  return null;
};

/**
 * Thera Advance is available when the company has a live banking rail (theraBanking + TP approved,
 * or dwollaBanking + Dwolla ready). Bypass companies skip this check entirely.
 */
export const canFundTheraAdvance = (company?: PayrollBankingCompany): boolean => {
  if (THERA_ADVANCE_BYPASS_COMPANY_IDS?.includes(company?.companyID)) return true;
  return getActivePayrollRail(company) !== null;
};

/** True when payroll runs through Thera banking (Dwolla or TP) rather than the legacy pay-in. */
export const isOnTheraPayrollRail = (company?: PayrollBankingCompany): boolean =>
  getActivePayrollRail(company) !== null;

/**
 * Companies on a Thera banking rail can't pay payroll until a rail is live. Companies on neither rail pay
 * through the legacy flow and are never blocked here.
 */
export const isPayrollBankingApproved = (company?: PayrollBankingCompany): boolean =>
  getOnboardingRail(company) === null || isOnTheraPayrollRail(company);

/** The KYB check's latest result on the company, until the Dwolla customer exists and Dwolla's verification takes over. */
export const getAlloyKybOnFile = (company?: PayrollBankingCompany): AlloyKybInformation | null =>
  company?.dwolla ? null : (company?.alloyKyb ?? null);

/**
 * The KYB check on file while the company onboards to Dwolla. A company moving over counts as onboarding to Dwolla
 * whatever rail it is on today. Null for a company Treasury Prime approved, which moves without the check.
 */
export const getAlloyKybBeforeDwolla = (company?: PayrollBankingCompany): AlloyKybInformation | null =>
  (getOnboardingRail(company) === 'DWOLLA' || isMovingToDwolla(company)) && !isTpPayrollApproved(company)
    ? getAlloyKybOnFile(company)
    : null;

/**
 * One request per document a reviewer can ask of an entity. A person can be asked for a photo ID and a proof of address
 * at once, and a request without a person kind means a photo ID, as on the backend.
 */
export const getAlloyRequestKey = ({
  externalEntityId,
  category,
  businessKind,
  personKind,
}: AlloyDocumentRequest): string => `${externalEntityId}:${category}:${businessKind ?? ''}:${personKind ?? 'PHOTO_ID'}`;

/**
 * Documents the KYB check's reviewers asked for, the open ones first. They only count while the check is still
 * deciding: once it approves or declines, or the Dwolla customer exists, nothing waits on them. The backend keeps every
 * past request, so only the latest one per document stays.
 */
export const getAlloyDocumentRequests = (company?: PayrollBankingCompany): AlloyDocumentRequest[] => {
  const alloyKyb = getAlloyKybBeforeDwolla(company);
  if (alloyKyb?.decision !== 'PENDING') return [];
  const latest = new Map<string, AlloyDocumentRequest>();
  // requestedAt is an ISO instant, which sorts as text.
  [...(alloyKyb.documentRequests ?? [])]
    .sort((a, b) => a.requestedAt.localeCompare(b.requestedAt))
    .forEach((request) => latest.set(getAlloyRequestKey(request), request));
  return [...latest.values()].sort((a, b) => Number(a.status !== 'OPEN') - Number(b.status !== 'OPEN'));
};

/** The KYB check's reviewers are waiting on a document nobody has sent yet. */
export const hasOpenAlloyDocumentRequest = (company?: PayrollBankingCompany): boolean =>
  getAlloyDocumentRequests(company).some(({ status }) => status === 'OPEN');

/** The KYB check couldn't run on the details sent, so the admin corrects them and submits again. */
export const isAlloyKybRetryPending = (company?: PayrollBankingCompany): boolean =>
  getAlloyKybBeforeDwolla(company)?.decision === 'ERROR';

/** The KYB check declined the business, so only Thera support can move it forward. */
export const isAlloyKybDeclined = (company?: PayrollBankingCompany): boolean =>
  getAlloyKybBeforeDwolla(company)?.decision === 'DENIED';

/**
 * The company still has to submit its banking (KYB) details. For TP this reads only the TP stage, as it always
 * has, because TP info is seeded on every US company including ones on neither rail.
 */
export const isPayrollBankingOnboardingNotStarted = (company?: PayrollBankingCompany): boolean => {
  if (getOnboardingRail(company) === 'DWOLLA') return !company?.dwolla && !company?.alloyKyb;
  return company?.tp?.payrollFboOnboardingStage === 'BANKING_DETAILS';
};

/**
 * The company has a banking record on its onboarding rail. On Dwolla that's the customer, not the KYB check: until the
 * customer exists the admin can still correct details Dwolla refused.
 */
export const hasPayrollBankingRecord = (company?: PayrollBankingCompany): boolean =>
  getOnboardingRail(company) === 'DWOLLA' ? Boolean(company?.dwolla) : Boolean(company?.tp);

/** Why payroll is blocked after the company has submitted its details, or null when it isn't. */
export const getPayrollBankingReviewStatus = (company?: PayrollBankingCompany): PayrollBankingReviewStatus | null => {
  if (isPayrollBankingApproved(company)) return null;

  if (getOnboardingRail(company) === 'DWOLLA') {
    const stage = company?.dwolla?.onboardingStage;
    if (stage) return DWOLLA_REVIEW_STATUS[stage];
    const alloyKyb = getAlloyKybBeforeDwolla(company);
    return alloyKyb ? ALLOY_KYB_REVIEW_STATUS[alloyKyb.decision] : null;
  }

  // TP rejections are handled by ops, so the customer keeps seeing review copy.
  const stage = company?.tp?.payrollFboOnboardingStage;
  return stage === 'SUBMITTED' || stage === 'DISBURSEMENT_ACCOUNT_REJECTED' ? 'IN_REVIEW' : null;
};

/**
 * Where the KYB check stands for a company moving to Dwolla before its Dwolla customer exists. Payroll keeps running on
 * the old rail meanwhile, so this never blocks it.
 */
export const getDwollaMoveKybStatus = (company?: PayrollBankingCompany): AlloyKybReviewStatus | null => {
  if (!isMovingToDwolla(company)) return null;
  const alloyKyb = getAlloyKybBeforeDwolla(company);
  return alloyKyb ? ALLOY_KYB_REVIEW_STATUS[alloyKyb.decision] : null;
};

/**
 * TP only: the details are in but the application hasn't reached review yet, or was rejected. On Dwolla the KYB
 * check's status covers the time before the customer exists.
 */
export const isTpApplicationAwaitingReview = (company?: PayrollBankingCompany): boolean => {
  if (getOnboardingRail(company) !== 'TREASURY_PRIME' || !company?.tp) return false;
  const stage = company.tp.payrollFboOnboardingStage;
  return stage !== 'DISBURSEMENT_ACCOUNT_APPROVED' && stage !== 'SUBMITTED';
};

/**
 * The rail holding the company's Thera Cash. Bulk pay draws cash from whichever rail is live, so that wins.
 * Before any rail is live it is Dwolla for Dwolla companies, and TP for everyone else because a TP deposit
 * account can exist without TP payroll.
 */
export const getTheraCashRail = (company?: PayrollBankingCompany): PayrollBankingRail =>
  getActivePayrollRail(company) ?? (company?.dwollaBanking === true ? 'DWOLLA' : 'TREASURY_PRIME');

/** The company has opened a Thera Cash account on its cash rail, approved or not. */
export const hasTheraCashAccount = (company?: PayrollBankingCompany): boolean =>
  getTheraCashRail(company) === 'DWOLLA' ? Boolean(company?.dwolla?.cashAccount) : Boolean(company?.tp?.depositAccount);

/** The company's Thera Cash account can hold and move funds. */
export const isTheraCashApproved = (company?: PayrollBankingCompany): boolean =>
  getTheraCashRail(company) === 'DWOLLA'
    ? isDwollaAccountReady(company?.dwolla?.cashAccount)
    : company?.tp?.depositAccount?.depositFboOnboardingStage === 'DEPOSIT_ACCOUNT_APPROVED';

/**
 * The company opens its Thera Cash account on Dwolla from the app. Dwolla opens the cash account only once the
 * payroll customer exists, so without it enabling Thera Cash only saves the setting.
 */
export const canOpenDwollaCashAccount = (company?: PayrollBankingCompany): boolean =>
  getTheraCashRail(company) === 'DWOLLA' && Boolean(company?.dwolla);

export type DwollaCashSetupStep = 'FINISH_SIGN_UP' | 'OPEN_ACCOUNT';

/**
 * What a company with Thera Cash on Dwolla still has to do before there's an application to review: sign up with
 * Dwolla, or open the cash account (enabling Banking can save the setting and still fail to open it). Null once
 * the account exists, and while the KYB check holds the payroll account, since the verification page owns that step.
 */
export const getDwollaCashSetupStep = (company?: PayrollBankingCompany): DwollaCashSetupStep | null => {
  if (getTheraCashRail(company) !== 'DWOLLA' || company?.dwolla?.cashAccount) return null;
  if (getAlloyKybBeforeDwolla(company)) return null;
  return company?.dwolla ? 'OPEN_ACCOUNT' : 'FINISH_SIGN_UP';
};

/** Dwolla only gives a verified cash account its own account and routing numbers. */
export const canHaveDwollaCashAccountNumbers = (company?: PayrollBankingCompany): boolean =>
  getTheraCashRail(company) === 'DWOLLA' && isTheraCashApproved(company);

/** Bulk pay can draw on Thera Cash: the cash account can move funds and a payroll rail is live to receive them. */
export const canPayPayrollFromTheraCash = (company?: PayrollBankingCompany): boolean =>
  isTheraCashApproved(company) && getActivePayrollRail(company) !== null;

/**
 * The company's Dwolla account of this kind is live, so its balance and transfers can be read. Dwolla answers
 * 400 for both until the account is verified and has a balance.
 */
export const isDwollaAccountActive = (
  company: PayrollBankingCompany | undefined,
  account: DwollaAccountKind,
): boolean =>
  account === 'CASH'
    ? getTheraCashRail(company) === 'DWOLLA' && isTheraCashApproved(company)
    : getOnboardingRail(company) === 'DWOLLA' && isDwollaAccountReady(company?.dwolla);

/**
 * Dwolla only moves cash between the cash account and the company's own verified banks. TP can also send it to
 * any ACH or wire recipient.
 */
export const isTheraCashWithdrawalToOwnBanksOnly = (company?: PayrollBankingCompany): boolean =>
  getTheraCashRail(company) === 'DWOLLA';

// Dwolla verification fixes. Each rule reads the company record the backend refreshes from Dwolla, so the
// screens only offer what Dwolla currently accepts.

const SOLE_PROPRIETORSHIP = 'soleProprietorship';
const RETRY_WITH_FULL_SSN = 'retry-with-full-ssn';
const OWNER_RETRY_ACTION = 'retry-verification';

/** What the admin does next to unblock payroll. Each one opens its own screen. */
export type PayrollBankingFix =
  'FINISH_ACCOUNT_SETUP' | 'CERTIFY_OWNERS' | 'RESUBMIT_DETAILS' | 'FIX_OWNERS' | 'UPLOAD_DOCUMENTS';

export interface DwollaAccountEntry {
  kind: DwollaAccountKind;
  account: DwollaAccount;
}

/**
 * The company's Dwolla accounts, payroll first. Empty until the Dwolla customer exists. A company still moving to
 * Dwolla has them too, so the fixes Dwolla asks for show up before payroll switches over.
 */
export const getDwollaAccounts = (company?: PayrollBankingCompany): DwollaAccountEntry[] => {
  if (!company?.dwolla) return [];
  const { cashAccount, ...payroll } = company.dwolla;
  const accounts: DwollaAccountEntry[] = [{ kind: 'PAYROLL', account: payroll }];
  if (cashAccount) accounts.push({ kind: 'CASH', account: cashAccount });
  return accounts;
};

/** When one of the company's Dwolla accounts was opened, as an ISO string, or undefined before it exists. */
export const getDwollaAccountOpenedAt = (
  company: PayrollBankingCompany | undefined,
  kind: DwollaAccountKind,
): string | undefined => {
  const createdAt = getDwollaAccounts(company).find((entry) => entry.kind === kind)?.account.createdAt;
  return createdAt instanceof Date ? createdAt.toISOString() : createdAt;
};

const isAwaitingBusinessRetry = (account: DwollaAccount): boolean => account.customerStatus === 'RETRY';

/**
 * Dwolla wants a second and last attempt at verifying the business, so the admin goes back through the
 * sign-up wizard. An owner in incomplete status also reads as NEEDS_RETRY, but that owner is fixed on its own.
 */
export const isPayrollBankingResubmissionPending = (company?: PayrollBankingCompany): boolean =>
  getDwollaAccounts(company).some(({ account }) => isAwaitingBusinessRetry(account));

/**
 * The admin goes back through the sign-up wizard to correct their details: Dwolla wants its second attempt, or the
 * KYB check couldn't run on what was sent. Only the Dwolla one is a retry with Dwolla; the other submits afresh.
 */
export const isBankingDetailsResubmissionPending = (company?: PayrollBankingCompany): boolean =>
  isPayrollBankingResubmissionPending(company) || isAlloyKybRetryPending(company);

export const canUpdateOwnersWithoutTp = (company?: PayrollBankingCompany): boolean =>
  isTpPayrollApproved(company) && UPDATE_OWNERS_WITHOUT_TP_COMPANY_IDS.includes(company?.companyID ?? '');

export const isDwollaSoleProprietorship = (account: DwollaAccount): boolean =>
  account.businessType === SOLE_PROPRIETORSHIP;

/**
 * The resubmission must carry the controller's full 9-digit SSN instead of the last four. Dwolla asks for it outright,
 * and the backend always needs it to retry a sole proprietorship.
 */
export const requiresFullControllerSsn = (company?: PayrollBankingCompany): boolean =>
  getDwollaAccounts(company).some(
    ({ account }) =>
      isAwaitingBusinessRetry(account) &&
      (isDwollaSoleProprietorship(account) || Boolean(account.verificationActions?.includes(RETRY_WITH_FULL_SSN))),
  );

const uniqueDirectives = (directives: DwollaVerificationDirective[]): DwollaVerificationDirective[] =>
  directives.filter(
    (directive, index) =>
      directives.findIndex((other) => other.code === directive.code && other.message === directive.message) === index,
  );

/** What Dwolla said is wrong with the business, for every account waiting on a resubmission. */
export const getDwollaRetryDirectives = (company?: PayrollBankingCompany): DwollaVerificationDirective[] =>
  uniqueDirectives(
    getDwollaAccounts(company)
      .filter(({ account }) => isAwaitingBusinessRetry(account))
      .flatMap(({ account }) => account.verificationDirectives ?? [])
      .filter(({ message }) => message.trim() !== ''),
  );

// Dwolla asks for these about the controller, but a resubmission retries the business without the controller and the
// backend has no field for a foreign passport, so resubmitting can't answer them.
const DWOLLA_DIRECTIVES_RESUBMISSION_CANNOT_ANSWER: readonly string[] = ['ForeignPassportNumberRequired'];

/**
 * Dwolla's retry is the last attempt, so when it asks for something a resubmission can't send, only Thera support can
 * finish the verification and the admin isn't offered the resubmission.
 */
export const isDwollaResubmissionBlocked = (company?: PayrollBankingCompany): boolean =>
  getDwollaAccounts(company).some(
    ({ account }) =>
      isAwaitingBusinessRetry(account) &&
      (account.verificationDirectives ?? []).some(({ code }) =>
        DWOLLA_DIRECTIVES_RESUBMISSION_CANNOT_ANSWER.includes(code),
      ),
  );

/**
 * Dwolla needs the admin to certify owners. Adding or removing an owner moves a certified account back to
 * recertify, which the stage does not show, so this reads the ownership status itself.
 */
export const isDwollaOwnershipCertificationPending = (company?: PayrollBankingCompany): boolean =>
  getDwollaAccounts(company).some(
    ({ account }) =>
      !isDwollaSoleProprietorship(account) &&
      account.beneficialOwnershipStatus !== undefined &&
      account.beneficialOwnershipStatus !== 'CERTIFIED',
  );

/** Matches the backend: these legal structures never list beneficial owners on Dwolla. */
const LEGAL_STRUCTURES_WITHOUT_OWNERS: readonly string[] = ['soleprop', 'trust', 'nonprofit'];

export const canHaveDwollaBeneficialOwners = (company?: PayrollBankingCompany): boolean => {
  const legalStructure = company?.companyBankingDetails?.legalStructure;
  return Boolean(legalStructure) && !LEGAL_STRUCTURES_WITHOUT_OWNERS.includes(legalStructure);
};

// Matches the backend: Dwolla checks the control person and owners from 25% ownership.
export const DWOLLA_BENEFICIAL_OWNER_MIN_PERCENT = 25;

interface DwollaOwnerFields {
  nationality?: string;
  citizenship?: string;
  ownershipPercentage?: number;
  identificationNumberType?: string;
  physicalAddress?: { country?: string };
}

// Like the backend, an owner without a citizenship is judged by the country they live in, once that's known.
export const isNonUsOwner = (owner: DwollaOwnerFields): boolean => {
  if (owner.nationality === 'NonUS') return true;
  if (owner.citizenship) return owner.citizenship !== 'US';
  return Boolean(owner.physicalAddress?.country) && owner.physicalAddress.country !== 'US';
};

/** Dwolla identifies an owner outside the US only by passport, so an owner saved with another ID has to replace it. */
export const needsDwollaPassport = (owner: DwollaOwnerFields): boolean =>
  isNonUsOwner(owner) && owner.identificationNumberType !== 'PASSPORT';

/** Adding an owner to an existing Dwolla account: only owners at 25% or more. Null when Dwolla would take them. */
export const getDwollaBeneficialOwnerProblem = (owner: DwollaOwnerFields): string | null => {
  if ((owner.ownershipPercentage ?? 0) < DWOLLA_BENEFICIAL_OWNER_MIN_PERCENT) {
    return `Only owners with ${DWOLLA_BENEFICIAL_OWNER_MIN_PERCENT}% or more ownership need to be added.`;
  }
  return null;
};

export const canRetryDwollaOwner = (owner: DwollaBeneficialOwnerInfo): boolean =>
  owner.verificationStatus === 'INCOMPLETE' && (owner.verificationActions?.includes(OWNER_RETRY_ACTION) ?? true);

/**
 * Who or what a document is for. Controller, business and DBA documents go on the Dwolla customer, owner ones on the
 * owner.
 */
export type DwollaDocumentParty = 'CONTROLLER' | 'BUSINESS' | 'DBA' | 'OWNER';

// The backend's DwollaKybService decides which documents Dwolla gets and refuses the rest, so these rules copy it
// exactly. Change both together.
const CONTROLLER_DOCUMENT_ACTIONS = ['verify-with-document', 'verify-controller-and-business-with-document'];
const BUSINESS_DOCUMENT_ACTIONS = ['verify-business-with-document', 'verify-controller-and-business-with-document'];
const PERSONAL_ID_DIRECTIVE = 'PersonalIDRequired';
const DBA_DOCUMENT_ACTION = 'upload-dba-document';
const DIRECTIVE_BUSINESS_KIND: Record<string, KybBusinessDocumentKind> = {
  EINDocumentRequired: 'EIN_LETTER',
  BusinessFormationDocRequired: 'FORMATION',
  BusinessAddressDocRequired: 'PROOF_OF_ADDRESS',
  CoWorkingAddress: 'PROOF_OF_ADDRESS',
  AddressNotAssociatedWithBusiness: 'PROOF_OF_ADDRESS',
};

export interface DwollaCustomerDocumentRequest {
  party: Exclude<DwollaDocumentParty, 'OWNER'>;
  /** The business document this request wants, when Dwolla named one. */
  businessKind?: KybBusinessDocumentKind;
  directives: DwollaVerificationDirective[];
}

/**
 * The customer-level documents Dwolla is asking for on one account, with the directives that explain why. A directive
 * that names a business document asks for it whatever action it came with, and once Dwolla named any business
 * document, those are all the business wants: each is its own request.
 */
export const getDwollaCustomerDocumentRequests = (account: DwollaAccount): DwollaCustomerDocumentRequest[] => {
  const actions = account.verificationActions ?? [];
  const directives = account.verificationDirectives ?? [];
  const hasAction = (wanted: string[]) => actions.some((action) => wanted.includes(action));
  // Directives that name no document only explain the request their action belongs to.
  const explains =
    (wanted: string[]) =>
    ({ code, actions: directiveActions }: DwollaVerificationDirective) =>
      !DIRECTIVE_BUSINESS_KIND[code] && directiveActions.some((action) => wanted.includes(action));
  const requests: DwollaCustomerDocumentRequest[] = [];

  if (hasAction(CONTROLLER_DOCUMENT_ACTIONS) || directives.some(({ code }) => code === PERSONAL_ID_DIRECTIVE)) {
    requests.push({
      party: 'CONTROLLER',
      directives: directives.filter(
        (directive) => directive.code === PERSONAL_ID_DIRECTIVE || explains(['verify-with-document'])(directive),
      ),
    });
  }

  const businessDirectives = directives.filter(explains(BUSINESS_DOCUMENT_ACTIONS));
  const businessKinds = [
    ...new Set([
      ...directives.map(({ code }) => DIRECTIVE_BUSINESS_KIND[code]).filter(Boolean),
      ...(actions.includes(DBA_DOCUMENT_ACTION) ? (['DBA'] as const) : []),
    ]),
  ];
  if (businessKinds.length === 0) {
    if (hasAction(BUSINESS_DOCUMENT_ACTIONS)) requests.push({ party: 'BUSINESS', directives: businessDirectives });
    return requests;
  }
  businessKinds.forEach((businessKind, index) =>
    requests.push({
      party: businessKind === 'DBA' ? 'DBA' : 'BUSINESS',
      businessKind,
      // The directives that name no document explain the first request rather than repeating on each.
      directives: [
        ...directives.filter(({ code }) => DIRECTIVE_BUSINESS_KIND[code] === businessKind),
        ...(index === 0 ? businessDirectives : []),
      ],
    }),
  );
  return requests;
};

/**
 * The account has customer-level documents in play: Dwolla is asking for one, or is reviewing one. The status
 * covers a review with no upload link left on the customer.
 */
export const hasDwollaCustomerDocuments = (account: DwollaAccount): boolean =>
  account.customerStatus === 'DOCUMENT' || getDwollaCustomerDocumentRequests(account).length > 0;

export const getDwollaDocumentOwners = (account: DwollaAccount): DwollaBeneficialOwnerInfo[] =>
  (account.beneficialOwners ?? []).filter((owner) => owner.verificationStatus === 'DOCUMENT');

/** Some account needs a document uploaded or reviewed. */
export const hasDwollaDocumentWork = (company?: PayrollBankingCompany): boolean =>
  getDwollaAccounts(company).some(
    ({ account }) => hasDwollaCustomerDocuments(account) || getDwollaDocumentOwners(account).length > 0,
  );

/** Where a document goes: the Dwolla customer of one account, or one of its owners. */
export interface DwollaDocumentTarget {
  account: DwollaAccountKind;
  /** Absent for business and controller documents, which belong to the Dwolla customer itself. */
  beneficialOwnerId?: string;
}

export interface DwollaDocumentRequestTarget extends DwollaDocumentTarget {
  party: DwollaDocumentParty;
  businessKind?: KybBusinessDocumentKind;
}

/**
 * Dwolla already has a document for this request and is reviewing it. Dwolla goes on asking for one meanwhile, so
 * only the uploaded documents can tell, read with `useDwollaDocumentsInReview`.
 */
export type IsDwollaDocumentInReview = (request: DwollaDocumentRequestTarget) => boolean;

/** One document Dwolla wants on one of the company's Dwolla accounts, and Dwolla's reasons when it gave any. */
export interface DwollaDocumentUploadRequest extends DwollaDocumentRequestTarget {
  ownerName?: string;
  isSoleProprietorship: boolean;
  directives: DwollaVerificationDirective[];
}

export const getDwollaDocumentRequestKey = ({
  account,
  party,
  businessKind,
  beneficialOwnerId,
}: DwollaDocumentRequestTarget): string => `${account}-${party}-${businessKind ?? ''}-${beneficialOwnerId ?? ''}`;

/** Every document Dwolla has asked for and isn't reviewing one for yet, read from its verification actions. */
export const getDwollaDocumentUploadRequests = (
  company: PayrollBankingCompany | undefined,
  isInReview: IsDwollaDocumentInReview,
): DwollaDocumentUploadRequest[] =>
  getDwollaAccounts(company).flatMap(({ kind, account }): DwollaDocumentUploadRequest[] => {
    const isSoleProprietorship = isDwollaSoleProprietorship(account);
    return [
      ...getDwollaCustomerDocumentRequests(account).map(({ party, businessKind, directives }) => ({
        account: kind,
        party,
        businessKind,
        isSoleProprietorship,
        directives,
      })),
      ...getDwollaDocumentOwners(account).map((owner) => ({
        account: kind,
        party: 'OWNER' as const,
        beneficialOwnerId: owner.id,
        ownerName: `${owner.firstName} ${owner.lastName}`,
        isSoleProprietorship,
        directives: [],
      })),
    ].filter((request) => !isInReview(request));
  });

/** Dwolla has asked for a document it doesn't have yet, as opposed to reviewing one already sent. */
export const hasDwollaDocumentUploadRequested = (
  company: PayrollBankingCompany | undefined,
  isInReview: IsDwollaDocumentInReview,
): boolean => getDwollaDocumentUploadRequests(company, isInReview).length > 0;

/** Payroll waits on documents Dwolla is still reviewing, with nothing for the admin to upload. */
export const isDwollaDocumentReviewPending = (
  company: PayrollBankingCompany | undefined,
  isInReview: IsDwollaDocumentInReview,
): boolean =>
  getPayrollBankingReviewStatus(company) === 'NEEDS_DOCUMENTS' &&
  !hasDwollaDocumentUploadRequested(company, isInReview);

/** Some owner has details Dwolla could not verify. */
export const hasDwollaOwnerToFix = (company?: PayrollBankingCompany): boolean =>
  getDwollaAccounts(company).some(({ account }) => (account.beneficialOwners ?? []).some(canRetryDwollaOwner));

// Dwolla's own name for each document type, which its document list reports.
const DWOLLA_DOCUMENT_TYPE_NAME: Record<DwollaDocumentType, string> = {
  LICENSE: 'license',
  ID_CARD: 'idCard',
  PASSPORT: 'passport',
  OTHER: 'other',
};

/** The document type behind a name from Dwolla's document list, or undefined for one we don't take. */
export const fromDwollaDocumentTypeName = (name: string): DwollaDocumentType | undefined =>
  (Object.keys(DWOLLA_DOCUMENT_TYPE_NAME) as DwollaDocumentType[]).find(
    (type) => DWOLLA_DOCUMENT_TYPE_NAME[type] === name,
  );

const PERSON_DOCUMENT_TYPES: readonly DwollaDocumentType[] = ['LICENSE', 'ID_CARD', 'PASSPORT'];
const BUSINESS_DOCUMENT_TYPES: readonly DwollaDocumentType[] = ['OTHER'];
const IMAGE_FILE_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];

export const DWOLLA_DOCUMENT_MAX_SIZE_MB = 10;

const isPersonParty = (party: DwollaDocumentParty): boolean => party === 'CONTROLLER' || party === 'OWNER';

/** A business or trade name document, as opposed to a person's ID. */
export const isBusinessDocumentParty = (party: DwollaDocumentParty): boolean => !isPersonParty(party);

// The KYB check can ask a person for a proof of address or an SSN document instead of a photo ID. Those go as OTHER,
// like a business document, and can be a PDF.
const isPhotoIdUpload = (party: DwollaDocumentParty, personKind?: KybPersonDocumentKind): boolean =>
  isPersonParty(party) && (personKind ?? 'PHOTO_ID') === 'PHOTO_ID';

export const getDwollaDocumentTypes = (
  party: DwollaDocumentParty,
  personKind?: KybPersonDocumentKind,
): readonly DwollaDocumentType[] =>
  isPhotoIdUpload(party, personKind) ? PERSON_DOCUMENT_TYPES : BUSINESS_DOCUMENT_TYPES;

/** Photo IDs must be images. Any other document can also be a PDF. */
export const getDwollaDocumentFileTypes = (party: DwollaDocumentParty, personKind?: KybPersonDocumentKind): string[] =>
  isPhotoIdUpload(party, personKind) ? IMAGE_FILE_TYPES : [...IMAGE_FILE_TYPES, 'application/pdf'];

/**
 * The next fix for a company whose payroll is blocked on Dwolla, or whose move to Dwolla waits on the KYB check. Null
 * when the admin has nothing to do.
 */
export const getPayrollBankingFix = (
  company: PayrollBankingCompany | undefined,
  isInReview: IsDwollaDocumentInReview,
): PayrollBankingFix | null => {
  switch (getPayrollBankingReviewStatus(company) ?? getDwollaMoveKybStatus(company)) {
    case 'IN_REVIEW':
      return hasOpenAlloyDocumentRequest(company) ? 'UPLOAD_DOCUMENTS' : null;
    case 'NEEDS_ACCOUNT_SETUP':
      return 'FINISH_ACCOUNT_SETUP';
    case 'NEEDS_CERTIFICATION':
      return 'CERTIFY_OWNERS';
    case 'NEEDS_RETRY':
      if (isDwollaResubmissionBlocked(company)) return null;
      return isBankingDetailsResubmissionPending(company) ? 'RESUBMIT_DETAILS' : 'FIX_OWNERS';
    case 'NEEDS_DOCUMENTS':
      return hasDwollaDocumentUploadRequested(company, isInReview) ? 'UPLOAD_DOCUMENTS' : null;
    default:
      return null;
  }
};

// Matches the backend's blocked statuses: both mean only Thera support can bring the account back.
const ON_HOLD_CUSTOMER_STATUSES: readonly DwollaCustomerStatus[] = ['SUSPENDED', 'DEACTIVATED'];

/** Dwolla accounts on hold, which only Thera support can reactivate. Payroll also reads its review stage. */
export const getDwollaAccountsOnHold = (company?: PayrollBankingCompany): DwollaAccountEntry[] =>
  getDwollaAccounts(company).filter(
    ({ kind, account }) =>
      ON_HOLD_CUSTOMER_STATUSES.includes(account.customerStatus) ||
      (kind === 'PAYROLL' && getPayrollBankingReviewStatus(company) === 'ON_HOLD'),
  );

/** The Thera Cash account on Dwolla is on hold. */
export const isDwollaCashAccountOnHold = (company?: PayrollBankingCompany): boolean =>
  getDwollaAccountsOnHold(company).some(({ kind }) => kind === 'CASH');

/** Anything on the verification page the admin can act on or is waiting on: the KYB check, or a Dwolla fix or review. */
const isVerificationWorkBesidesDocumentsPending = (company?: PayrollBankingCompany): boolean =>
  getAlloyKybBeforeDwolla(company) !== null ||
  isDwollaOwnershipCertificationPending(company) ||
  isPayrollBankingResubmissionPending(company) ||
  hasDwollaOwnerToFix(company) ||
  getDwollaAccountsOnHold(company).length > 0;

export const isBankingVerificationWorkPending = (company?: PayrollBankingCompany): boolean =>
  isVerificationWorkBesidesDocumentsPending(company) || hasDwollaDocumentWork(company);

/** All that's left is Dwolla reviewing documents it already has, so the admin has nothing to do. */
export const isOnlyDwollaDocumentReviewPending = (
  company: PayrollBankingCompany | undefined,
  isInReview: IsDwollaDocumentInReview,
): boolean =>
  hasDwollaDocumentWork(company) &&
  !isVerificationWorkBesidesDocumentsPending(company) &&
  !hasDwollaDocumentUploadRequested(company, isInReview);

interface DwollaLinkableBank {
  dwolla?: { payrollLinked: boolean; cashLinked: boolean };
}

/**
 * Dwolla can only use banks verified through Plaid, not cards or micro-deposit banks. Those are the banks that report
 * a Dwolla status.
 */
export const isDwollaLinkableBank = (bank?: DwollaLinkableBank): boolean => Boolean(bank?.dwolla);

/** One of the company's banks can fund payroll on Dwolla. */
export const hasDwollaPayrollBank = (banks?: DwollaLinkableBank[]): boolean =>
  Boolean(banks?.some((bank) => bank.dwolla?.payrollLinked));

export type PayrollBankAction<Bank> = { kind: 'AUTHORIZE'; bank: Bank } | { kind: 'ADD' };

/**
 * What the admin still has to do before a bank can fund payroll on Dwolla: authorize a bank they added, or add one
 * Dwolla can link when none can. A bank added during onboarding can't be authorized then, since the Dwolla account only
 * opens once the KYB check approves. A company moving from TP needs it too, since ops can only switch it over once its
 * Dwolla account is ready and a bank can fund payroll. `banks` is undefined while the bank list is unknown.
 */
export const getPayrollBankAction = <Bank extends DwollaLinkableBank>(
  company: PayrollBankingCompany | undefined,
  banks: Bank[] | undefined,
): PayrollBankAction<Bank> | undefined => {
  const canLinkPayrollBank = isMovingToDwolla(company)
    ? isDwollaAccountReady(company?.dwolla)
    : isDwollaPayingPayroll(company);
  if (!canLinkPayrollBank || !banks || hasDwollaPayrollBank(banks)) return undefined;
  const bank = banks.find((method) => needsDwollaBankAuthorization(company, method, 'PAYROLL'));
  if (bank) return { kind: 'AUTHORIZE', bank };
  return banks.some(isDwollaLinkableBank) ? undefined : { kind: 'ADD' };
};

/**
 * The bank isn't linked to this Dwolla account with an on-demand authorization yet, so the admin has to agree to
 * Dwolla's authorization before the bank can fund or receive money there. Banks Dwolla can't link report no status.
 */
export const needsDwollaBankAuthorization = (
  company: PayrollBankingCompany | undefined,
  bank: DwollaLinkableBank | undefined,
  account: DwollaAccountKind,
): boolean => {
  if (!isDwollaLinkableBank(bank)) return false;
  if (account === 'CASH') {
    return getTheraCashRail(company) === 'DWOLLA' && Boolean(company?.dwolla?.cashAccount) && !bank.dwolla.cashLinked;
  }
  // A company still moving to Dwolla links its payroll bank too, so ops can switch it over once Dwolla verifies it.
  return Boolean(company?.dwolla) && !bank.dwolla.payrollLinked;
};

/**
 * A Dwolla company can only pay invoices from a bank Dwolla can use. Autopay on a Dwolla company is held to this from
 * the start, because it moves to Dwolla the day Dwolla goes live.
 */
export const canPayInvoicesFromBank = (
  company: PayrollBankingCompany | undefined,
  bank: DwollaLinkableBank | undefined,
): boolean => company?.dwollaBanking !== true || isDwollaLinkableBank(bank);

export const DWOLLA_PLAID_ONLY_BANK_MESSAGE = 'Only banks connected through Plaid can pay invoices';

// Mirrors the backend: a manual payment goes through Dwolla, and links the bank there first, only once Dwolla is the
// live payroll rail. Until then it goes through the company's live rail and any bank works.
const isDwollaPayingPayroll = (company?: PayrollBankingCompany): boolean => getActivePayrollRail(company) === 'DWOLLA';

/** Why the pay modal can't charge this bank, or null when it can. */
export const getManualPayBankBlocker = (
  company: PayrollBankingCompany | undefined,
  bank: DwollaLinkableBank | undefined,
): string | null =>
  isDwollaPayingPayroll(company) && !canPayInvoicesFromBank(company, bank) ? DWOLLA_PLAID_ONLY_BANK_MESSAGE : null;

/** A manual payment from this bank needs the admin's Dwolla authorization first. */
export const needsDwollaAuthorizationToPay = (
  company: PayrollBankingCompany | undefined,
  bank: DwollaLinkableBank | undefined,
): boolean => isDwollaPayingPayroll(company) && needsDwollaBankAuthorization(company, bank, 'PAYROLL');

/** The code the backend puts at the start of its error when a bank still needs the admin's authorization. */
const DWOLLA_AUTHORIZATION_REQUIRED = 'DWOLLA_AUTHORIZATION_REQUIRED';

export const isDwollaAuthorizationRequiredError = (message?: string): boolean =>
  Boolean(message?.includes(DWOLLA_AUTHORIZATION_REQUIRED));

/** How the backend starts its refusal to create the Dwolla customer while it requires an approved KYB check. */
const ALLOY_KYB_NOT_APPROVED = 'Alloy KYB must be approved';

export const isAlloyKybNotApprovedError = (message?: string): boolean =>
  Boolean(message?.includes(ALLOY_KYB_NOT_APPROVED));
